/**
 * Module: Macrothemes Switcher
 * Variante editoriale interattiva della sezione "Esplora i macrotemi".
 * Lo scambio tra card usa la tecnica FLIP sugli elementi reali del DOM.
 * Scoped via [data-macrothemes-switcher] → inerte dove la sezione non esiste.
 */

const DEFAULT_AUTOPLAY_INTERVAL = 5000;
const MIN_AUTOPLAY_INTERVAL = 3000;
const MAX_AUTOPLAY_INTERVAL = 30000;
const DEFAULT_ANIMATION_DURATION = 600;
const MIN_ANIMATION_DURATION = 300;
const MAX_ANIMATION_DURATION = 2000;
const ANIMATION_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const MOBILE_VIEWPORT_QUERY = '(max-width: 767.98px)';

export const initMacrothemesSwitcher = () => {
    const sections = document.querySelectorAll('[data-macrothemes-switcher]');

    if (sections.length === 0) return;

    sections.forEach((section) => {
        const cardsContainer = section.querySelector('.macrotemi-switch');
        let cards = Array.from(section.querySelectorAll('[data-macrotheme-card]'));

        if (!cardsContainer || cards.length === 0) return;

        const autoplayToggle = section.querySelector('[data-macrothemes-autoplay-toggle]');
        const autoplayLabel = autoplayToggle
            ? autoplayToggle.querySelector('[data-macrothemes-autoplay-label]')
            : null;
        const pauseIcon = autoplayToggle
            ? autoplayToggle.querySelector('[data-macrothemes-pause-icon]')
            : null;
        const playIcon = autoplayToggle
            ? autoplayToggle.querySelector('[data-macrothemes-play-icon]')
            : null;
        const pauseLabel = autoplayToggle
            ? (autoplayToggle.dataset.pauseLabel || '').trim()
            : '';
        const resumeLabel = autoplayToggle
            ? (autoplayToggle.dataset.resumeLabel || '').trim()
            : '';
        const hasAutoplayLabels = pauseLabel !== '' && resumeLabel !== '';
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        const mobileViewport = window.matchMedia(MOBILE_VIEWPORT_QUERY);
        const configuredInterval = Number.parseInt(section.dataset.macrothemesInterval, 10);
        const autoplayInterval = Number.isFinite(configuredInterval)
            ? Math.min(MAX_AUTOPLAY_INTERVAL, Math.max(MIN_AUTOPLAY_INTERVAL, configuredInterval))
            : DEFAULT_AUTOPLAY_INTERVAL;
        const configuredDuration = Number.parseInt(section.dataset.macrothemesDuration, 10);
        const animationDuration = Number.isFinite(configuredDuration)
            ? Math.min(MAX_ANIMATION_DURATION, Math.max(MIN_ANIMATION_DURATION, configuredDuration))
            : DEFAULT_ANIMATION_DURATION;
        const autoplayEnabled = section.dataset.macrothemesAutoplay !== 'false' && hasAutoplayLabels;

        cardsContainer.style.setProperty('--macrotemi-switch-duration', `${animationDuration}ms`);

        let activeCard = cards.find((card) => (
            card.dataset.macrothemeCard === section.dataset.activeMacrotheme
        )) || cards.find((card) => card.classList.contains('is-active')) || cards[0];
        let autoplayTimer = null;
        let activeAnimations = [];
        let isAnimating = false;
        let isPointerInside = false;
        let isFocusInside = false;
        let isPausedByUser = false;

        const isMotionDisabled = () => reducedMotion.matches || mobileViewport.matches;

        const clearAutoplay = () => {
            if (autoplayTimer === null) return;

            window.clearTimeout(autoplayTimer);
            autoplayTimer = null;
        };

        const canAutoplay = () => (
            autoplayEnabled
            && cards.length > 1
            && !isMotionDisabled()
            && !isAnimating
            && !isPointerInside
            && !isFocusInside
            && !isPausedByUser
            && !document.hidden
        );

        const updateAutoplayToggle = () => {
            if (!autoplayToggle) return;

            const showToggle = autoplayEnabled && !isMotionDisabled() && cards.length > 1;
            autoplayToggle.hidden = !showToggle;
            autoplayToggle.setAttribute('aria-pressed', isPausedByUser ? 'true' : 'false');
            autoplayToggle.title = isPausedByUser ? resumeLabel : pauseLabel;

            if (pauseIcon) {
                pauseIcon.toggleAttribute('hidden', isPausedByUser);
            }

            if (playIcon) {
                playIcon.toggleAttribute('hidden', !isPausedByUser);
            }

            if (autoplayLabel) {
                autoplayLabel.textContent = isPausedByUser ? resumeLabel : pauseLabel;
            }
        };

        const captureCardRects = () => new Map(
            cards.map((card) => [card, card.getBoundingClientRect()])
        );

        const applyCardState = (selectedCard, previousActiveCard) => {
            const remainingCards = cards.filter((card) => (
                card !== selectedCard && card !== previousActiveCard
            ));
            const nextOrder = [selectedCard, ...remainingCards];

            if (previousActiveCard && previousActiveCard !== selectedCard) {
                nextOrder.push(previousActiveCard);
            }

            cards = nextOrder;
            activeCard = selectedCard;

            cards.forEach((card) => {
                const isActive = card === selectedCard;
                const trigger = card.querySelector('[data-macrotheme-trigger]');

                card.classList.toggle('is-active', isActive);
                card.classList.toggle('is-compact', !isActive);
                cardsContainer.appendChild(card);

                if (trigger) {
                    trigger.setAttribute('aria-pressed', isActive ? 'true' : 'false');
                }
            });

            section.dataset.activeMacrotheme = selectedCard.dataset.macrothemeCard;
        };

        const scheduleAutoplay = () => {
            clearAutoplay();

            if (!canAutoplay()) return;

            autoplayTimer = window.setTimeout(() => {
                autoplayTimer = null;
                const nextCard = cards.find((card) => card !== activeCard);

                if (nextCard) {
                    swapCards(nextCard); // eslint-disable-line no-use-before-define
                }
            }, autoplayInterval);
        };

        const finishAnimation = (promotedCard, demotedCard) => {
            activeAnimations.forEach((animation) => animation.cancel());
            activeAnimations = [];
            promotedCard.classList.remove('is-promoting');

            if (demotedCard) {
                demotedCard.classList.remove('is-demoting');
            }

            section.classList.remove('is-animating');
            section.removeAttribute('aria-busy');
            isAnimating = false;
            scheduleAutoplay();
        };

        const swapCards = async (selectedCard) => {
            if (isAnimating || !selectedCard || selectedCard === activeCard) return;

            clearAutoplay();

            const previousActiveCard = activeCard;
            const supportsAnimation = !isMotionDisabled()
                && cards.every((card) => typeof card.animate === 'function');
            const firstRects = supportsAnimation ? captureCardRects() : null;

            isAnimating = true;
            section.classList.add('is-animating');
            section.setAttribute('aria-busy', 'true');
            selectedCard.classList.add('is-promoting');
            previousActiveCard.classList.add('is-demoting');

            applyCardState(selectedCard, previousActiveCard);

            if (!supportsAnimation) {
                finishAnimation(selectedCard, previousActiveCard);
                return;
            }

            const lastRects = captureCardRects();

            activeAnimations = cards.map((card) => {
                const firstRect = firstRects.get(card);
                const lastRect = lastRects.get(card);
                const deltaX = firstRect.left - lastRect.left;
                const deltaY = firstRect.top - lastRect.top;
                const scaleX = lastRect.width ? firstRect.width / lastRect.width : 1;
                const scaleY = lastRect.height ? firstRect.height / lastRect.height : 1;

                return card.animate(
                    [
                        {
                            transform: `translate(${deltaX}px, ${deltaY}px) scale(${scaleX}, ${scaleY})`,
                            transformOrigin: 'top left',
                        },
                        {
                            transform: 'translate(0, 0) scale(1, 1)',
                            transformOrigin: 'top left',
                        },
                    ],
                    {
                        duration: animationDuration,
                        easing: ANIMATION_EASING,
                        fill: 'both',
                    }
                );
            });

            await Promise.all(
                activeAnimations.map((animation) => animation.finished.catch(() => undefined))
            );

            finishAnimation(selectedCard, previousActiveCard);
        };

        applyCardState(activeCard, null);

        cards.forEach((card) => {
            const trigger = card.querySelector('[data-macrotheme-trigger]');

            if (!trigger) return;

            trigger.addEventListener('click', () => {
                swapCards(card);
            });
        });

        section.addEventListener('mouseenter', () => {
            isPointerInside = true;
            clearAutoplay();
        });

        section.addEventListener('mouseleave', () => {
            isPointerInside = false;
            scheduleAutoplay();
        });

        section.addEventListener('focusin', () => {
            isFocusInside = true;
            clearAutoplay();
        });

        section.addEventListener('focusout', () => {
            window.requestAnimationFrame(() => {
                isFocusInside = section.contains(document.activeElement);

                if (!isFocusInside) {
                    scheduleAutoplay();
                }
            });
        });

        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                clearAutoplay();
                return;
            }

            scheduleAutoplay();
        });

        if (autoplayToggle) {
            autoplayToggle.addEventListener('click', () => {
                isPausedByUser = !isPausedByUser;
                updateAutoplayToggle();

                if (isPausedByUser) {
                    clearAutoplay();
                    return;
                }

                scheduleAutoplay();
            });
        }

        const handleMotionPreferenceChange = () => {
            clearAutoplay();

            if (isMotionDisabled() && activeAnimations.length > 0) {
                activeAnimations.forEach((animation) => animation.cancel());
            }

            updateAutoplayToggle();
            scheduleAutoplay();
        };

        if (typeof reducedMotion.addEventListener === 'function') {
            reducedMotion.addEventListener('change', handleMotionPreferenceChange);
        } else {
            reducedMotion.addListener(handleMotionPreferenceChange);
        }

        if (typeof mobileViewport.addEventListener === 'function') {
            mobileViewport.addEventListener('change', handleMotionPreferenceChange);
        } else {
            mobileViewport.addListener(handleMotionPreferenceChange);
        }

        updateAutoplayToggle();
        scheduleAutoplay();
    });
};
